import { useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { throttle } from 'lodash-es';
import { gql } from '@soundxyz/gql-string';
import { useAuth } from '@context/auth/base';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import type { FragmentType } from '@graphql/reactQuery';
import {
  AuthUserDocument,
  fetchGQL,
  getFragment,
  UpdateReferralWithdrawableBalanceDocument,
  UpdateReferralWithdrawableInfoFragmentDoc,
} from '@graphql/reactQuery';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UpdateReferralWithdrawableInfo on WithdrawAffiliateEarnings {
    id
    amount
  }
`);

export const updateReferralWithdrawable = throttle(
  () => {
    fetchGQL(UpdateReferralWithdrawableBalanceDocument, {}).catch(err => {
      Sentry.captureException(err, {
        level: 'warning',
      });
    });
  },
  5000,
  {
    leading: true,
    trailing: false,
  },
);

RefetchOnComplete({
  trigger: [UpdateReferralWithdrawableBalanceDocument],
  refetch: [AuthUserDocument],
});

export function useUpdateReferralWithdrawableBalance({
  data,
}: {
  data: FragmentType<UpdateReferralWithdrawableInfoFragmentDoc>[];
}) {
  const { authUser } = useAuth();

  const referralWithdrawableAmount = useMemo(
    () =>
      data
        .map(d => BigInt(getFragment(UpdateReferralWithdrawableInfoFragmentDoc, d).amount))
        .reduce((acc, cur) => acc + cur, 0n)
        .toString(),
    [data],
  );

  const { lastReferralWithdrawableBalance = null } = authUser || {};

  useEffect(() => {
    if (!lastReferralWithdrawableBalance) return;

    if (lastReferralWithdrawableBalance !== referralWithdrawableAmount) {
      updateReferralWithdrawable();
    }
  }, [lastReferralWithdrawableBalance, referralWithdrawableAmount]);
}
